import React, { createContext, ReactNode, useContext, useEffect, useReducer } from 'react';
import { AuthState, LoginRequest, LoginResponse, RefreshTokenResponse, User } from '../types';

const AUTH_API_BASE_URL = process.env.REACT_APP_AUTH_API_URL || 'https://mainapi.talkappdev.com';
const FRONTEND_DEBUG = process.env.REACT_APP_FRONTEND_DEBUG === 'TRUE';

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth reducer
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkAuth = async (): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');

    console.log('FRONTEND_DEBUG', FRONTEND_DEBUG);
    console.log('token', token ? '***' : null);
    console.log('userData', userData ? 'present' : null);
    if (token && userData) {
      try {
        const user: User = JSON.parse(userData);
        
        // Debug mode: skip token verification
        if (FRONTEND_DEBUG && token.startsWith('debug_token_')) {
          console.log('🔧 DEBUG MODE: Skipping token verification');
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user, token },
          });
          return;
        }
        
        // If AUTH_API_BASE_URL is not configured, trust stored credentials
        if (!AUTH_API_BASE_URL) {
          console.warn('AUTH_API_BASE_URL not configured — trusting stored session');
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user, token },
          });
          return;
        }
        
        // Normal authentication: verify token is still valid
        try {
          const response = await fetch(`${AUTH_API_BASE_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const currentUser: User = await response.json();
            
            // Ensure user is still admin and valid
            if (currentUser.role === 'admin' && currentUser.valid) {
              // Update stored user data with fresh data
              localStorage.setItem('user_data', JSON.stringify(currentUser));
              dispatch({
                type: 'LOGIN_SUCCESS',
                payload: { user: currentUser, token },
              });
            } else {
              logout();
            }
          } else if (response.status === 401) {
            // Token expired — try refreshing before giving up
            console.warn('Auth token expired, attempting refresh...');
            try {
              const refreshResponse = await fetch(`${AUTH_API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });

              if (refreshResponse.ok) {
                const refreshData: RefreshTokenResponse = await refreshResponse.json();
                const newToken = refreshData.token;
                localStorage.setItem('auth_token', newToken);

                // Re-verify with the new token
                const retryResponse = await fetch(`${AUTH_API_BASE_URL}/auth/me`, {
                  headers: { 'Authorization': `Bearer ${newToken}` },
                });

                if (retryResponse.ok) {
                  const currentUser: User = await retryResponse.json();
                  if (currentUser.role === 'admin' && currentUser.valid) {
                    localStorage.setItem('user_data', JSON.stringify(currentUser));
                    dispatch({
                      type: 'LOGIN_SUCCESS',
                      payload: { user: currentUser, token: newToken },
                    });
                  } else {
                    logout();
                  }
                } else {
                  logout();
                }
              } else {
                // Refresh also failed — force re-login
                logout();
              }
            } catch (refreshError) {
              console.warn('Token refresh failed, trusting stored session:', refreshError);
              // Network issue during refresh — trust stored session
              dispatch({
                type: 'LOGIN_SUCCESS',
                payload: { user, token },
              });
            }
          } else {
            // Server error / temporary issue — trust stored session
            console.warn(`Auth API returned ${response.status}, trusting stored session`);
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: { user, token },
            });
          }
        } catch (networkError) {
          // Network failure — trust stored session instead of logging out
          console.warn('Auth API unreachable, trusting stored session:', networkError);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user, token },
          });
        }
      } catch (error) {
        console.error('Auth check error:', error);
        logout();
      }
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Check if user is already authenticated on app start
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      // Debug mode: bypass authentication
      if (FRONTEND_DEBUG) {
        console.log('🔧 DEBUG MODE: Bypassing authentication');
        
        const debugUser: User = {
          id: 1,
          profile_path: '',
          email: 'debug@example.com',
          firstName: 'Debug',
          lastName: 'Admin',
          username: 'debug_admin',
          role: 'admin',
          valid: true,
          langs: '',
          languageId: 1,
          country_id: 1,
          timezone: 'UTC',
          timeFormat: 'h24',
          mobile_1: '',
          mobile_2: '',
          phone_1: '',
          phone_2: '',
          vonage_id: '',
          whatsapp_notice_accepted: false,
          is_public_profile_active: false
        };
        
        const debugToken = 'debug_token_' + Date.now();
        
        // Store debug token and user data
        localStorage.setItem('auth_token', debugToken);
        localStorage.setItem('user_data', JSON.stringify(debugUser));
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: debugUser, token: debugToken },
        });
        return;
      }
      
      // Normal authentication flow
      // Add role parameter for admin login; captcha is required by the auth API
      if (!credentials.captcha) {
        throw new Error('Captcha token missing');
      }

      const loginData = {
        email: credentials.email,
        password: credentials.password,
        role: 'admin',
        captcha: credentials.captcha,
      };

      const response = await fetch(`${AUTH_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      if (!response.ok) {
        if (response.status === 400) {
          throw new Error('Captcha verification failed');
        }
        if (response.status === 429) {
          throw new Error('Too many login attempts. Please try again later.');
        }
        throw new Error('Login failed');
      }

      const data: LoginResponse = await response.json();
      
      // Get user information using the token
      const userResponse = await fetch(`${AUTH_API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${data.token}`,
        },
      });

      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }

      const user: User = await userResponse.json();
      
      // Check if user is admin
      if (user.role !== 'admin') {
        throw new Error('Access denied. Admin role required.');
      }

      // Store token in localStorage
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_data', JSON.stringify(user));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token: data.token },
      });
    } catch (error) {
      console.error('Login error:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = (): void => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    dispatch({ type: 'LOGOUT' });
  };

  const refreshToken = async (): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('No token to refresh');
    }

    try {
      const response = await fetch(`${AUTH_API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data: RefreshTokenResponse = await response.json();
      
      // Update stored token
      localStorage.setItem('auth_token', data.token);
      
      // Update the token in the current state if user is authenticated
      if (state.isAuthenticated && state.user) {
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: state.user, token: data.token },
        });
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout(); // Logout if refresh fails
      throw error;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkAuth,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
